import React, { useContext } from 'react'
import { Link } from 'react-router-dom'
import { Badge, IconButton, Tooltip } from '@mui/material'
// import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import { withStyles } from '@mui/styles'

import { useWidth } from '../../../../core/utils'
import LogoNedgiaFull from '../../../../resources/images/logo_nedgia_full.svg'
import LogoNedgia from '../../../../resources/images/logo_nedgia.svg'
import { MenuComponent as Menu } from '../../../menu'
import { NotificationDrawer } from '../../../../features/notifications'
import { DEFAULT_ROUTE, MENU_ITEMS, PROFILE_ROUTE } from '../constants'
import { privateLayoutStyles } from './private-layout.styles'
import { NotificationsContext } from '../../../../features/notifications/contexts'
import { APP_VERSION_MSSG } from '../../public-layout/constants'
import { NoticesHistoryComponent } from '../../../../features/notices/components/notices-history'

/**
 * @typedef {object} PrivateLayoutComponentProps
 * @property {React.ReactNode} children - El contenido principal de la página que se renderizará en el área del cuerpo.
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 */

/**
 * Componente de layout para páginas privadas (autenticadas) de la aplicación.
 * 
 * Proporciona la estructura completa de la interfaz para usuarios autenticados, incluyendo:
 * - **Header con logo**: Logo de Nedgia responsive (versión completa o reducida según el tamaño de pantalla)
 * - **Menú de navegación**: Menú principal con items configurables y soporte para vista móvil
 * - **Iconos de acción**: Notificaciones, avisos y perfil de usuario
 * - **Drawer de notificaciones**: Panel lateral para gestionar notificaciones
 * - **Layout responsive**: Adaptación automática a diferentes tamaños de pantalla
 * 
 * El componente utiliza el contexto de notificaciones para mostrar badges con el número de
 * notificaciones no leídas y gestionar la apertura/cierre del drawer de notificaciones.
 * 
 * @component
 * @example
 * const MyPrivatePage = () => {
 *   return (
 *     <PrivateLayoutComponent>
 *       <div>
 *         <h1>Dashboard</h1>
 *         <p>Contenido de la página privada...</p>
 *       </div>
 *     </PrivateLayoutComponent>
 *   );
 * };
 * 
 * @param {PrivateLayoutComponentProps} props - Las props del componente PrivateLayoutComponent.
 */
export const PrivateLayoutComponent = withStyles(privateLayoutStyles)(({ children, classes }) => {
  let screenWidth = useWidth()
  const { notificationsData, toggleNotificationsShow } = useContext(NotificationsContext)
  const isMobileWidth = screenWidth === 'sm' || screenWidth === 'xs'

  const mobileView = MENU_ITEMS.length > 5
  return (
    <div className={classes.root}>
      <div className={classes.headerWrapper}>
        <div className={classes.headerLeftContent} style={mobileView ? { order: '2' } : {}}>
          <div className={classes.logo}>
            <Tooltip title={APP_VERSION_MSSG}>
              <Link to={DEFAULT_ROUTE}>
                {screenWidth === 'xs' ? (
                  <img src={LogoNedgia} alt={'nedgia logo'} />
                ) : (
                  <img
                    src={LogoNedgiaFull}
                    alt="Nedgia_Logo"
                    style={{ width: '100%', height: 'auto' }}
                  />
                )}
              </Link>
            </Tooltip>
          </div>
        </div>
        <div className={classes.menu} style={mobileView ? { order: '1' } : {}}>
          <Menu items={MENU_ITEMS} isMobileWidth={isMobileWidth} mobileView={mobileView} />
        </div>
        <div className={classes.headerRightContent} style={mobileView ? { order: '3' } : {}}>
          {/* <Link to={SETTINGS_ROUTE}>
            <IconButton className={classes.iconButton}>
              <SettingsOutlinedIcon />
            </IconButton>
          </Link>
           */}
          <NoticesHistoryComponent className={classes.iconButton} />
          <IconButton className={classes.iconButton} onClick={() => toggleNotificationsShow()}>
            <Badge
              variant="dot"
              color="error"
              invisible={notificationsData.filter((el) => !el.visto).length === 0}
            >
              <NotificationsOutlinedIcon />
            </Badge>
          </IconButton>

          <Link to={PROFILE_ROUTE}>
            <IconButton className={classes.iconButton}>
              <AccountCircleOutlinedIcon />
            </IconButton>
          </Link>
        </div>
      </div>
      <div className={classes.mainArea}>
        <div className={classes.bodyWrapper}>{children}</div>
        <NotificationDrawer />
      </div>
    </div>
  )
})
